import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { LogOut, RefreshCw, Mail, Phone, Users, CalendarRange, Inbox, Trash2, CalendarPlus, CheckCircle2, Undo2, Reply, XCircle, KeyRound, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Calendar } from "@/components/ui/calendar";
import { StatsPanel } from "./AdminStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vcr_admin_token";

function formatError(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` });

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset_token"));
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const forgot = async () => {
    if (!email) { toast.error("Saisissez d'abord votre adresse e-mail ci-dessus."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (newPwd !== confirmPwd) { toast.error("La confirmation ne correspond pas."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPwd });
      toast.success("Mot de passe réinitialisé. Vous pouvez maintenant vous connecter.");
      window.history.replaceState({}, "", "/admin");
      setResetToken(null);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (resetToken) {
    return (
      <div className="min-h-screen bg-sand-bg grain flex items-center justify-center px-6">
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
          onSubmit={doReset}
          className="w-full max-w-md"
          data-testid="reset-password-form"
        >
          <span className="text-[11px] tracking-[0.35em] uppercase text-terracotta">Espace propriétaire</span>
          <h1 className="font-serif font-light text-3xl md:text-4xl mt-4 mb-10 tracking-tight">Nouveau mot de passe</h1>
          <input
            data-testid="reset-new-password"
            type="password"
            placeholder="Nouveau mot de passe (8 caractères min.)"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            className="w-full bg-transparent border-b border-[var(--line)] py-3 mb-6 focus:outline-none focus:border-terracotta transition-colors"
          />
          <input
            data-testid="reset-confirm-password"
            type="password"
            placeholder="Confirmer le mot de passe"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            className="w-full bg-transparent border-b border-[var(--line)] py-3 mb-10 focus:outline-none focus:border-terracotta transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="reset-submit-btn"
            className="w-full bg-ink text-sand-bg py-4 text-sm tracking-wide hover:bg-terracotta transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Enregistrement..." : "Réinitialiser le mot de passe"}
          </button>
          <button
            type="button"
            onClick={() => { window.history.replaceState({}, "", "/admin"); setResetToken(null); }}
            className="block w-full text-center mt-6 text-sm text-ink-soft link-underline"
          >
            ← Retour à la connexion
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-bg grain flex items-center justify-center px-6">
      <motion.form
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
        onSubmit={submit}
        className="w-full max-w-md"
        data-testid="admin-login-form"
      >
        <span className="text-[11px] tracking-[0.35em] uppercase text-terracotta">Espace propriétaire</span>
        <h1 className="font-serif font-light text-4xl md:text-5xl mt-4 mb-10 tracking-tight">Villa Tutto Passa</h1>
        <input
          data-testid="admin-email"
          type="email"
          placeholder="Adresse e-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-transparent border-b border-[var(--line)] py-3 mb-6 focus:outline-none focus:border-terracotta transition-colors"
        />
        <input
          data-testid="admin-password"
          type="password"
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent border-b border-[var(--line)] py-3 mb-3 focus:outline-none focus:border-terracotta transition-colors"
        />
        <button
          type="button"
          onClick={forgot}
          data-testid="forgot-password-btn"
          className="block mb-8 text-sm text-ink-soft link-underline"
        >
          Mot de passe oublié ?
        </button>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="w-full bg-ink text-sand-bg py-4 text-sm tracking-wide hover:bg-terracotta transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? "Connexion..." : "Se connecter"}
        </button>
        <a href="/" className="block text-center mt-6 text-sm text-ink-soft link-underline">← Retour au site</a>
      </motion.form>
    </div>
  );
};

const fmt = (d) => { try { return format(new Date(d), "d MMM yyyy", { locale: fr }); } catch { return d; } };

const normalizeStatus = (s) => (s === "new" ? "pending" : s === "answered" ? "confirmed" : s);

const statusBadge = (it) => {
  const st = normalizeStatus(it.status);
  if (st === "declined") return { label: "Refusée", cls: "bg-ink/10 text-ink-soft" };
  if (st === "confirmed") {
    const today = format(new Date(), "yyyy-MM-dd");
    if (it.start_date && today < it.start_date) return { label: "Confirmée · À venir", cls: "bg-olive/15 text-olive" };
    if (it.start_date && it.end_date && today > it.end_date) return { label: "Séjour terminé", cls: "bg-ink/10 text-ink-soft" };
    if (it.start_date) return { label: "Séjour en cours", cls: "bg-olive/25 text-olive" };
    return { label: "Confirmée", cls: "bg-olive/15 text-olive" };
  }
  return { label: "En attente de confirmation", cls: "bg-terracotta/15 text-terracotta" };
};

const replyHref = (it) => {
  const dates = it.start_date
    ? `du ${fmt(it.start_date)}${it.end_date ? ` au ${fmt(it.end_date)}` : ""}`
    : "aux dates souhaitées";
  const subject = "Votre demande de séjour — Villa Tutto Passa";
  const body = `Bonjour ${it.first_name},

Merci pour votre demande de séjour ${dates} pour ${it.guests} voyageur${it.guests > 1 ? "s" : ""} à la Villa Tutto Passa.

[Votre réponse : disponibilités, tarif, conditions]

Bien cordialement,
Villa Tutto Passa
06 03 27 46 10`;
  return `mailto:${it.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const BookingsPanel = ({ onLogout, onChanged }) => {
  const [bookings, setBookings] = useState([]);
  const [range, setRange] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [event, setEvent] = useState("");
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { headers: authHeaders() });
      setBookings(data);
    } catch {
      toast.error("Impossible de charger les périodes réservées.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const bookedRanges = bookings.map((b) => ({
    from: new Date(`${b.start_date}T00:00:00`),
    to: new Date(`${b.end_date}T00:00:00`),
  }));

  const add = async () => {
    if (!range?.from) { toast.error("Sélectionnez un jour ou une période sur le calendrier."); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/bookings`,
        {
          start_date: format(range.from, "yyyy-MM-dd"),
          end_date: format(range.to || range.from, "yyyy-MM-dd"),
          amount: amount === "" ? null : Number(amount),
          last_name: lastName,
          first_name: firstName,
          event,
        },
        { headers: authHeaders() }
      );
      toast.success("Période bloquée dans le calendrier.");
      setRange(undefined);
      setAmount("");
      setLastName("");
      setFirstName("");
      setEvent("");
      load();
      onChanged?.();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: authHeaders() });
      toast.success("Période libérée.");
      load();
      onChanged?.();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Suppression impossible.");
    }
  };

  const startEdit = (b) => {
    setEditId(b.id);
    setEdit({
      start_date: b.start_date,
      end_date: b.end_date,
      amount: b.amount ?? "",
      last_name: b.last_name || "",
      first_name: b.first_name || "",
      event: b.event || "",
    });
  };

  const saveEdit = async () => {
    try {
      await axios.patch(
        `${API}/bookings/${editId}`,
        {
          start_date: edit.start_date,
          end_date: edit.end_date,
          amount: edit.amount === "" ? null : Number(edit.amount),
          last_name: edit.last_name,
          first_name: edit.first_name,
          event: edit.event,
        },
        { headers: authHeaders() }
      );
      toast.success("Réservation modifiée.");
      setEditId(null);
      load();
      onChanged?.();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error(formatError(err.response?.data?.detail));
    }
  };

  const selLabel = range?.from
    ? `${format(range.from, "d MMM yyyy", { locale: fr })}${range.to ? ` — ${format(range.to, "d MMM yyyy", { locale: fr })}` : ""}`
    : "Aucune sélection";

  const inputCls = "bg-transparent border border-[var(--line)] px-2 h-9 text-sm focus:outline-none focus:border-terracotta transition-colors";

  return (
    <section className="mb-12 bg-sand-surface border border-[var(--line)] p-6" data-testid="bookings-panel">
      <div className="flex items-center gap-3 mb-2">
        <CalendarRange className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl font-light">Disponibilités — périodes réservées</h2>
      </div>
      <p className="text-ink-soft text-sm mb-6">
        Sélectionnez un jour ou une période directement sur le calendrier, puis bloquez-la.
        Renseignez le nom du vacancier (prénom et événement facultatifs) et un montant (utilisé pour le chiffre d'affaires des statistiques).
      </p>

      <div className="flex flex-col gap-8">
        <div data-testid="admin-block-calendar">
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={2}
            locale={fr}
            modifiers={{ booked: bookedRanges }}
            modifiersClassNames={{ booked: "bg-terracotta/15 text-terracotta line-through" }}
            classNames={{
              day_outside: "invisible pointer-events-none",
              day_selected: "aria-selected:bg-terracotta aria-selected:text-white rounded-full font-medium",
              day_range_start: "aria-selected:bg-terracotta aria-selected:text-white rounded-full font-medium",
              day_range_end: "aria-selected:bg-terracotta aria-selected:text-white rounded-full font-medium",
              day_range_middle: "aria-selected:bg-terracotta/20 aria-selected:text-ink rounded-none",
            }}
            className="border border-[var(--line)] bg-sand-bg p-3"
          />
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <span className="text-sm text-ink-soft" data-testid="selection-label">{selLabel}</span>
            <input
              type="text"
              placeholder="Nom"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              data-testid="booking-lastname-input"
              className={`${inputCls} w-36`}
            />
            <input
              type="text"
              placeholder="Prénom (facultatif)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="booking-firstname-input"
              className={`${inputCls} w-40`}
            />
            <input
              type="text"
              placeholder="Événement (facultatif)"
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              data-testid="booking-event-input"
              className={`${inputCls} w-44`}
            />
            <input
              type="number"
              min="0"
              placeholder="Montant € (optionnel)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="booking-amount-input"
              className={`${inputCls} w-44`}
            />
            <button
              onClick={add}
              disabled={saving || !range?.from}
              data-testid="add-booking-btn"
              className="flex items-center gap-2 bg-ink text-sand-bg px-5 h-10 text-sm hover:bg-terracotta transition-colors disabled:opacity-50"
            >
              <CalendarPlus className="w-4 h-4" /> Bloquer cette période
            </button>
            {range?.from && (
              <button onClick={() => setRange(undefined)} data-testid="clear-selection-btn" className="text-sm text-ink-soft link-underline">
                Effacer la sélection
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs tracking-[0.2em] uppercase text-ink-soft mb-3">Périodes bloquées</p>
          {bookings.length === 0 ? (
            <p className="text-ink-soft text-sm" data-testid="no-bookings">Aucune période réservée pour l'instant.</p>
          ) : (
            <ul className="divide-y divide-[var(--line)]" data-testid="bookings-list">
              {bookings.map((b) => (
                <li key={b.id} className="py-3 text-sm" data-testid="booking-item">
                  {editId === b.id ? (
                    <div className="flex flex-wrap items-center gap-2" data-testid={`edit-booking-${b.id}`}>
                      <input type="date" value={edit.start_date} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} data-testid="edit-start-date" className={inputCls} />
                      <input type="date" value={edit.end_date} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} data-testid="edit-end-date" className={inputCls} />
                      <input type="text" placeholder="Nom" value={edit.last_name} onChange={(e) => setEdit({ ...edit, last_name: e.target.value })} data-testid="edit-lastname" className={`${inputCls} w-28`} />
                      <input type="text" placeholder="Prénom" value={edit.first_name} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} data-testid="edit-firstname" className={`${inputCls} w-28`} />
                      <input type="text" placeholder="Événement" value={edit.event} onChange={(e) => setEdit({ ...edit, event: e.target.value })} data-testid="edit-event" className={`${inputCls} w-32`} />
                      <input type="number" min="0" placeholder="Montant €" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} data-testid="edit-amount" className={`${inputCls} w-28`} />
                      <button onClick={saveEdit} data-testid="save-edit-btn" className="bg-ink text-sand-bg px-4 h-9 text-xs hover:bg-terracotta transition-colors">Enregistrer</button>
                      <button onClick={() => setEditId(null)} data-testid="cancel-edit-btn" className="text-xs text-ink-soft link-underline">Annuler</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-ink min-w-0">
                        {fmt(b.start_date)} — {fmt(b.end_date)}
                        {(b.last_name || b.first_name) && <span className="font-medium"> · {[b.last_name, b.first_name].filter(Boolean).join(" ")}</span>}
                        {b.event && <span className="text-ink-soft"> · {b.event}</span>}
                        {b.amount != null && <span className="text-olive"> · {Number(b.amount).toLocaleString("fr-FR")} €</span>}
                        {b.inquiry_id && <span className="text-[10px] uppercase tracking-wider text-terracotta ml-2">via site</span>}
                        {b.source === "presence" && <span className="text-[10px] uppercase tracking-wider text-olive ml-2">Conciergerie</span>}
                      </span>
                      {b.source === "presence" ? (
                        <span className="text-xs text-ink-soft shrink-0">Synchronisé — presencehomeservices.com</span>
                      ) : (
                      <span className="flex items-center gap-4 shrink-0">
                        <button
                          onClick={() => startEdit(b)}
                          data-testid={`edit-booking-btn-${b.id}`}
                          className="flex items-center gap-1.5 text-ink-soft hover:text-ink transition-colors"
                        >
                          <Pencil className="w-4 h-4" /> Modifier
                        </button>
                        <button
                          onClick={() => remove(b.id)}
                          data-testid={`delete-booking-${b.id}`}
                          className="flex items-center gap-1.5 text-ink-soft hover:text-terracotta transition-colors"
                        >
                          <Trash2 className="w-4 h-4" /> Libérer
                        </button>
                      </span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

const PasswordPanel = ({ onLogout, onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères."); return; }
    if (next !== confirm) { toast.error("La confirmation ne correspond pas."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: authHeaders() });
      toast.success("Mot de passe modifié avec succès.");
      setCurrent(""); setNext(""); setConfirm("");
      onClose();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = "bg-transparent border border-[var(--line)] px-3 h-10 text-sm focus:outline-none focus:border-terracotta transition-colors w-full sm:w-56";

  return (
    <section className="mb-12 bg-sand-surface border border-[var(--line)] p-6" data-testid="password-panel">
      <div className="flex items-center gap-3 mb-6">
        <KeyRound className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl font-light">Changer mon mot de passe</h2>
      </div>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3" data-testid="password-form">
        <label className="flex flex-col gap-1 text-xs text-ink-soft">
          Mot de passe actuel
          <input type="password" data-testid="pwd-current" value={current} onChange={(e) => setCurrent(e.target.value)} className={field} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-soft">
          Nouveau mot de passe (8 caractères min.)
          <input type="password" data-testid="pwd-new" value={next} onChange={(e) => setNext(e.target.value)} className={field} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-soft">
          Confirmer
          <input type="password" data-testid="pwd-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} />
        </label>
        <button type="submit" disabled={saving} data-testid="pwd-submit" className="flex items-center gap-2 bg-ink text-sand-bg px-5 h-10 text-sm hover:bg-terracotta transition-colors disabled:opacity-60">
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>
    </section>
  );
};

const Dashboard = ({ onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showPwd, setShowPwd] = useState(false);
  const [bookingsKey, setBookingsKey] = useState(0);
  const [selected, setSelected] = useState([]);

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selected.length} demande${selected.length > 1 ? "s" : ""} ?`)) return;
    try {
      const { data } = await axios.post(`${API}/inquiries/bulk-delete`, { ids: selected }, { headers: authHeaders() });
      toast.success(`${data.deleted} demande${data.deleted > 1 ? "s" : ""} supprimée${data.deleted > 1 ? "s" : ""}.`);
      setSelected([]);
      load();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Suppression impossible.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inquiries`, { headers: authHeaders() });
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Impossible de charger les demandes.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/inquiries/${id}/status`, { status }, { headers: authHeaders() });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      setBookingsKey((k) => k + 1);
      const msgs = {
        confirmed: data.calendar_blocked
          ? "Demande confirmée — e-mail envoyé au vacancier, dates bloquées au calendrier."
          : "Demande confirmée — e-mail envoyé au vacancier.",
        declined: "Demande refusée.",
        pending: "Demande remise en attente — dates libérées du calendrier.",
      };
      toast.success(msgs[status] || "Statut mis à jour.");
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Mise à jour impossible.");
    }
  };

  const active = items.filter((it) => normalizeStatus(it.status) !== "declined");
  const archived = items.filter((it) => normalizeStatus(it.status) === "declined");
  const pending = active.filter((it) => normalizeStatus(it.status) === "pending").length;
  const shown =
    filter === "declined"
      ? archived
      : active.filter((it) => (filter === "all" ? true : normalizeStatus(it.status) === filter));

  return (
    <div className="min-h-screen bg-sand-bg grain">
      <header className="border-b border-[var(--line)] sticky top-0 bg-sand-bg/80 backdrop-blur-xl z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-terracotta">Espace propriétaire</p>
            <p className="font-serif text-2xl font-light">Villa Tutto Passa</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPwd((v) => !v)} data-testid="pwd-toggle-btn" className="flex items-center gap-2 border border-[var(--line)] px-4 h-10 text-sm hover:border-ink transition-colors">
              <KeyRound className="w-4 h-4" /> Mot de passe
            </button>
            <button onClick={load} data-testid="refresh-btn" className="w-10 h-10 border border-[var(--line)] flex items-center justify-center hover:border-ink transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onLogout} data-testid="logout-btn" className="flex items-center gap-2 border border-[var(--line)] px-4 h-10 text-sm hover:border-ink transition-colors">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {showPwd && <PasswordPanel onLogout={onLogout} onClose={() => setShowPwd(false)} />}
        <BookingsPanel key={bookingsKey} onLogout={onLogout} onChanged={() => setBookingsKey((k) => k + 1)} />
        <StatsPanel onLogout={onLogout} refreshKey={bookingsKey} />

        <h2 className="font-serif text-2xl font-light mb-2">Demandes de réservation</h2>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <p className="text-ink-soft text-sm" data-testid="inquiries-count">
            {active.length} demande{active.length > 1 ? "s" : ""} reçue{active.length > 1 ? "s" : ""}
            {pending > 0 && <span className="text-terracotta"> · {pending} à traiter</span>}
            {archived.length > 0 && <span> · {archived.length} archivée{archived.length > 1 ? "s" : ""}</span>}
          </p>
          <div className="flex gap-2" data-testid="status-filters">
            {[
              { key: "all", label: "Toutes" },
              { key: "pending", label: "En attente" },
              { key: "confirmed", label: "Confirmées" },
              { key: "declined", label: `Archivées (refusées)${archived.length ? ` · ${archived.length}` : ""}` },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`text-xs px-4 py-2 border transition-colors ${
                  filter === f.key ? "bg-ink text-sand-bg border-ink" : "border-[var(--line)] text-ink-soft hover:border-ink hover:text-ink"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-32 text-ink-soft">
            <Inbox className="w-10 h-10 mb-4" strokeWidth={1} />
            <p className="font-serif text-2xl font-light">Aucune demande pour l'instant</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="bulk-actions">
          <label className="flex items-center gap-2 text-sm text-ink-soft cursor-pointer">
            <input
              type="checkbox"
              checked={shown.length > 0 && selected.length === shown.length}
              onChange={(e) => setSelected(e.target.checked ? shown.map((i) => i.id) : [])}
              data-testid="select-all-checkbox"
              className="w-4 h-4 accent-[#C1694F]"
            />
            Tout sélectionner
          </label>
          {selected.length > 0 && (
            <button
              onClick={bulkDelete}
              data-testid="bulk-delete-btn"
              className="flex items-center gap-2 border border-terracotta text-terracotta px-4 h-9 text-sm hover:bg-terracotta hover:text-sand-bg transition-colors"
            >
              <Trash2 className="w-4 h-4" /> Supprimer ({selected.length})
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-5" data-testid="inquiries-list">
          {shown.map((it) => {
            const st = normalizeStatus(it.status);
            const badge = statusBadge(it);
            return (
            <motion.div
              key={it.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-sand-surface border p-6 ${st === "confirmed" ? "border-olive/40" : st === "declined" ? "border-[var(--line)] opacity-70" : "border-[var(--line)]"}`}
              data-testid="inquiry-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.includes(it.id)}
                    onChange={() => toggleSelect(it.id)}
                    data-testid={`select-inquiry-${it.id}`}
                    className="mt-2 w-4 h-4 accent-[#C1694F] cursor-pointer"
                  />
                  <h3 className="font-serif text-2xl font-light">{it.first_name} {it.last_name}</h3>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-xs text-ink-soft">{fmt(it.created_at)}</span>
                  <span
                    data-testid="inquiry-status-badge"
                    className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 ${badge.cls}`}
                  >
                    {badge.label}
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm text-ink-soft">
                <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-terracotta" /> <a href={`mailto:${it.email}`} className="link-underline">{it.email}</a></p>
                <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-terracotta" /> {it.phone}</p>
                <p className="flex items-center gap-2"><Users className="w-4 h-4 text-terracotta" /> {it.guests} voyageur{it.guests > 1 ? "s" : ""}</p>
                <p className="flex items-center gap-2"><CalendarRange className="w-4 h-4 text-terracotta" /> {it.start_date ? `${fmt(it.start_date)} — ${it.end_date ? fmt(it.end_date) : "?"}` : "Dates non précisées"}</p>
              </div>
              {it.message && <p className="mt-4 pt-4 border-t border-[var(--line)] text-ink text-sm leading-relaxed">{it.message}</p>}
              <div className="mt-5 flex flex-col sm:flex-row gap-2">
                <a
                  href={replyHref(it)}
                  data-testid={`reply-email-${it.id}`}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm border border-terracotta text-terracotta hover:bg-terracotta hover:text-sand-bg transition-colors duration-300"
                >
                  <Reply className="w-4 h-4" /> Répondre par e-mail
                </a>
                {st === "pending" ? (
                  <>
                    <button
                      onClick={() => setStatus(it.id, "confirmed")}
                      data-testid={`confirm-${it.id}`}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm bg-ink text-sand-bg border border-ink hover:bg-olive hover:border-olive transition-colors duration-300"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Confirmer
                    </button>
                    <button
                      onClick={() => setStatus(it.id, "declined")}
                      data-testid={`decline-${it.id}`}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm border border-[var(--line)] text-ink-soft hover:border-ink hover:text-ink transition-colors duration-300"
                    >
                      <XCircle className="w-4 h-4" /> Refuser
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setStatus(it.id, "pending")}
                    data-testid={`toggle-status-${it.id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm border border-[var(--line)] text-ink-soft hover:border-ink hover:text-ink transition-colors duration-300"
                  >
                    <Undo2 className="w-4 h-4" /> Remettre en attente
                  </button>
                )}
              </div>
            </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(null); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUser(r.data))
      .catch(() => { sessionStorage.removeItem(TOKEN_KEY); setUser(null); });
  }, []);

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setUser(null); };

  if (user === undefined) return <div className="min-h-screen bg-sand-bg" />;

  return (
    <>
      {user ? <Dashboard onLogout={logout} /> : <LoginView onLogin={setUser} />}
      <Toaster position="bottom-right" />
    </>
  );
}
